import React from 'react';
import { notifyBrokerageDataChanged } from 'Components/Banking/brokerageDataBus';
import { analyzeShortTerm } from 'Components/Marketdata/marketdataChartAnalysis';
import './Brokerage.css';

// Order-Formular fuer IB (Chat vom 2026-08-07, Phase 2 der IBKR-Integration).
// Kein Login/TAN-Dialog wie bei BrokerageOrderForm.js (Comdirect) - IB
// braucht keine SCA pro Order (siehe Plan "sleepy-whistling-waterfall").
// Stattdessen ein einfacher lokaler Bestaetigungsschritt (zwei Klicks statt
// einem), bevor tatsaechlich gesendet wird - kein Server-Roundtrip fuer eine
// "Challenge", da es keine gibt.
//
// props: x, target ({conId, symbol, secType, exchange, currency, action:
// 'BUY'|'SELL', orderId? (nur beim Aendern), label? (voller Name fuer die
// Ueberschrift, sonst Symbol), price? (aktueller Kurs, fuer die
// Limit-Vorbelegung), quoteSymbol?/bid?/ask? (fuer Realtime-Bid/Ask + Limit-
// Autovorbelegung, siehe Chat vom 2026-08-10)}), onDone(), onCancel().

// Gleitkomma-Artefakte entfernen (bis 4 Nachkommastellen) - IB liefert valide
// Tick-Groessen, unser Rohwert kann aber z.B. 3926.3849999999998 sein.
function roundTick(value) {
	return value == null ? null : Math.round(value * 10000) / 10000;
}

// Sinnvolle Schrittweite fuer die Pfeiltasten des Limit-Feldes je nach
// Kursgroesse (Chat vom 2026-08-11, Bugreport: "Pfeil runter" bei einer
// 0,58-Aktie ergab -0,42, weil step fest auf 1 stand). Grosse Futures/Indizes
// (>=1000) weiter in ganzen Schritten (Nutzerwunsch fuer MNQ & Co.), teurere
// Aktien in 0,1-Schritten, normale in Cent, Penny-Stocks noch feiner.
function limitStep(value) {
	const v = Math.abs(parseFloat(value));
	if (!Number.isFinite(v) || v === 0) return 0.01;
	if (v >= 1000) return 1;
	if (v >= 50) return 0.1;
	if (v >= 1) return 0.01;
	if (v >= 0.1) return 0.001;
	return 0.0001;
}

// Bid/Ask kompakt anzeigen - Nachkommastellen nach Groessenordnung.
function formatQuote(value) {
	if (value == null) return '–';
	const abs = Math.abs(value);
	const digits = abs >= 1000 ? 2 : abs >= 1 ? 2 : 4;
	return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

export default function IbBrokerageOrderForm(props) {
	const x = props.x;
	const target = props.target;
	const isChange = target.orderId != null;

	// Chat vom 2026-08-07 (Nutzerwunsch: "sowohl Kauf als auch Verkauf
	// moeglich, da IB Leerverkaeufe zulaesst") - target.action ist nur noch
	// der VORSCHLAG, mit dem der Dialog geoeffnet wurde (z.B. "Kaufen" beim
	// Klick auf den IB-Kauf-Button in der Watchlist), aendert sich aber
	// selbst nicht mehr - die tatsaechlich gesendete Order-Richtung kommt
	// jetzt aus diesem eigenen State (Radiobuttons unten), unabhaengig
	// davon, wie der Dialog geoeffnet wurde. Beim Aendern einer bestehenden
	// Order (isChange) bleibt die ursspruengliche Richtung der Order
	// unveraendert waehlbar - IB erlaubt kein Umdrehen der Richtung per
	// "Change", nur neue Order.
	const [action, setAction] = React.useState(target.action || 'BUY');
	// orderType: Vorbelegung "Limit" (Nutzerwunsch, Chat vom 2026-08-10). Beim
	// ÄNDERN einer bestehenden Order gewinnt deren tatsaechlicher Ordertyp
	// (target.orderType) - ein bestehender Market-Auftrag oeffnet also als
	// Market, ein Limit-Auftrag als Limit.
	const [orderType, setOrderType] = React.useState(target.orderType || 'LMT');
	// Realtime-Bid/Ask (Chat vom 2026-08-10) - Startwerte aus dem Ziel (der
	// zuletzt bekannten Quote), danach live ueber den Socket aktualisiert
	// (siehe useEffect unten). Nur verfuegbar, wenn das Formular aus der
	// Watchlist heraus geoeffnet wurde (target.quoteSymbol gesetzt).
	const [liveBid, setLiveBid] = React.useState(target.bid ?? null);
	const [liveAsk, setLiveAsk] = React.useState(target.ask ?? null);
	// Sobald der Nutzer das Limit-Feld fokussiert, wird die automatische
	// Bid/Ask-Vorbelegung eingefroren (er hat die Kontrolle uebernommen).
	const [limitTouched, setLimitTouched] = React.useState(false);
	// Kurzfrist-Ampel (Chat vom 2026-08-10) - eine charttechnische Analyse auf
	// 1-Minuten-Kerzen laeuft im Hintergrund, sobald der Dialog geoeffnet wird,
	// und gibt eine Ampel-Empfehlung fuer einen "absolut kurzfristigen" Trade:
	// grün = bullisch (Long), rot = bärisch (Short), gelb = neutral. null =
	// laeuft noch. Nur wenn aus der Watchlist geoeffnet (internes Symbol da).
	const [shortTerm, setShortTerm] = React.useState(null);
	// Chat vom 2026-08-07 (Nutzerwunsch) - sinnvolle Stueckzahl-Vorbelegung
	// statt eines leeren Feldes: bei Futures ist 1 Kontrakt der ueblichere
	// Einstieg (ein Kontrakt hat bereits einen Hebel/Multiplikator), bei
	// Aktien 100 Stueck (glatte, gaengige Positionsgroesse). Nur als
	// Vorschlag - bleibt frei editierbar. Beim Aendern einer bestehenden
	// Order (target.totalQuantity gesetzt) hat die tatsaechliche Order immer
	// Vorrang.
	const [quantity, setQuantity] = React.useState(target.totalQuantity || (target.secType === 'FUT' ? 1 : 100));
	// Limit-Vorbelegung mit dem aktuellen Kurs (Nutzerwunsch) - erspart das
	// manuelle Abtippen des gerade angezeigten Kurses als Ausgangspunkt,
	// bleibt aber frei editierbar. Nur gerundet auf 2 Nachkommastellen (wie
	// die uebrige Kursanzeige), IB rundet ohnehin auf die zulaessige
	// Tick-Groesse.
	const [limitPrice, setLimitPrice] = React.useState(target.limitPrice || (target.price != null ? Math.round(target.price * 100) / 100 : ''));
	const [confirming, setConfirming] = React.useState(false);
	const [sending, setSending] = React.useState(false);
	const [error, setError] = React.useState('');
	const [result, setResult] = React.useState(null);
	const limitInputRef = React.useRef(null);
	// Chat vom 2026-08-09 (Nutzerwunsch): eine abgesendete Order soll nach
	// ~2s automatisch nochmal auf ihren Status geprueft werden. Die
	// synchrone Antwort von IbBrokeragePlaceOrder liefert nur den ERSTEN
	// orderStatus (oft "PreSubmitted"/"Pending") - die schnellen Folge-
	// wechsel (Submitted/Filled) treffen wenige Sekundenbruchteile spaeter
	// als async orderStatus-Events ein und werden serverseitig in
	// ibBrokerageOrders gespiegelt. Timer-Handle, um den Nachschlag beim
	// Schliessen des Dialogs zuverlaessig abzubrechen (kein setState nach
	// Unmount).
	const recheckTimer = React.useRef(null);
	React.useEffect(() => {
		return () => {
			if (recheckTimer.current) {
				clearTimeout(recheckTimer.current);
			}
		};
	}, []);

	// Realtime-Bid/Ask abonnieren (Chat vom 2026-08-10) - selber Socket-Kanal
	// wie die Watchlist Live ("marketdata-quote"), gefiltert auf das interne
	// Symbol dieses Wertpapiers (target.quoteSymbol). Nur aktiv, wenn das
	// Formular aus der Watchlist kam.
	React.useEffect(() => {
		const connection = x.was.webSocket;
		if (!connection || !target.quoteSymbol) {
			return undefined;
		}
		const handler = (quote) => {
			if (!quote || quote.symbol !== target.quoteSymbol) {
				return;
			}
			if (quote.bidPrice != null) setLiveBid(quote.bidPrice);
			if (quote.askPrice != null) setLiveAsk(quote.askPrice);
		};
		connection.on('marketdata-quote', handler);
		return () => connection.off('marketdata-quote', handler);
	}, [x, target.quoteSymbol]);

	// Limit automatisch mit Bid/Ask vorbelegen, solange der Nutzer das Feld
	// noch nicht angefasst hat (Chat vom 2026-08-10). BUY -> Ask (aktueller
	// Kaufpreis), SELL -> Bid (aktueller Verkaufspreis), analog TWS. Beim
	// ÄNDERN einer Order NICHT automatisch ueberschreiben - dort ist das
	// bestehende Limit der Ausgangswert.
	React.useEffect(() => {
		if (isChange || limitTouched || orderType !== 'LMT') {
			return;
		}
		const side = action === 'BUY' ? liveAsk : liveBid;
		if (side != null) {
			setLimitPrice(roundTick(side));
		}
	}, [liveBid, liveAsk, action, orderType, limitTouched, isChange]);

	// Kurzfrist-Ampel im Hintergrund berechnen (Chat vom 2026-08-10) - beim
	// Oeffnen des Dialogs einmalig die 1-Minuten-Kerzen (Range "1m") holen und
	// per analyzeShortTerm() zu einer Ampel verdichten. Nur moeglich, wenn das
	// interne Watchlist-Symbol vorliegt (aus der Watchlist geoeffnet); beim
	// Aendern/Verkaufen aus dem Orderbuch/Depot fehlt es -> keine Ampel.
	React.useEffect(() => {
		if (!target.quoteSymbol) {
			return undefined;
		}
		let cancelled = false;
		setShortTerm(null);
		x.was.axPost(
			'Backend/webapp.php?module=Marketdata&task=MarketdataChart',
			{ symbol: target.quoteSymbol, range: '1m' },
			(res) => {
				if (cancelled) return;
				const candles = res.data.returncode === 1 ? (res.data.content || []) : [];
				setShortTerm(analyzeShortTerm(candles));
			},
			() => {
				if (!cancelled) setShortTerm({ ok: false });
			}
		);
		return () => {
			cancelled = true;
		};
	}, [x, target.quoteSymbol]);

	// Hinweis (Chat vom 2026-08-10): Das frühere automatische Fokussieren/
	// Markieren des Limit-Feldes bei Auswahl von "Limit" wurde ENTFERNT - es
	// haette den onFocus ausgeloest und damit die neue Bid/Ask-Autovorbelegung
	// sofort eingefroren. Das Feld folgt jetzt live dem Bid/Ask, bis der Nutzer
	// es selbst anklickt (siehe limitTouched).

	// Return loest den jeweils aktiven Primaerbutton aus (Chat vom 2026-08-10) -
	// erst "Weiter" (Bestaetigungsschritt), dann "Jetzt verbindlich senden",
	// exakt dieselbe Logik wie der Button-Klick (submit()). Nur wenn absendbar
	// (Menge vorhanden, nicht schon am Senden). Klick auf einen Button loest
	// Return ohnehin selbst aus - dort nicht zusaetzlich feuern, um kein
	// doppeltes submit() auszuloesen.
	function handleKeyDown(e) {
		if (e.key !== 'Enter' || e.target.tagName === 'BUTTON') {
			return;
		}
		if (sending || !quantity) {
			return;
		}
		e.preventDefault();
		submit();
	}

	function submit() {
		if (!confirming) {
			setConfirming(true);
			return;
		}
		setSending(true);
		setError('');
		const task = isChange ? 'IbBrokerageChangeOrder' : 'IbBrokeragePlaceOrder';
		const body = {
			conId: target.conId,
			symbol: target.symbol,
			secType: target.secType,
			exchange: target.exchange,
			currency: target.currency,
			action,
			orderType,
			totalQuantity: quantity,
			limitPrice: orderType === 'LMT' ? limitPrice : undefined,
		};
		if (isChange) {
			body.orderId = target.orderId;
		}
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=' + task, body, (res) => {
			setSending(false);
			const content = res.data.content || {};
			if (res.data.returncode !== 1) {
				setError(content.error || 'Order fehlgeschlagen - IB-Trading ist vermutlich nicht freigeschaltet.');
				return;
			}
			if (content.error) {
				setError(content.error);
				return;
			}
			setResult(content);
			notifyBrokerageDataChanged(x);
			// Nachschlag nach 2s (siehe recheckTimer oben): den inzwischen
			// gespiegelten, aktuelleren Status der gerade gesendeten Order
			// holen und im Ergebnis-Screen anzeigen sowie Orderbuch/Depot
			// erneut auffrischen. Ueber die orderId aus der Sofort-Antwort.
			if (content.orderId != null) {
				recheckTimer.current = setTimeout(() => {
					recheckOrderStatus(content.orderId);
				}, 2000);
			}
		});
	}

	function recheckOrderStatus(orderId) {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageOrderList', {}, (res) => {
			if (res.data.returncode !== 1) {
				return;
			}
			const orders = res.data.content.orders || [];
			const match = orders.find((o) => String(o.orderid) === String(orderId));
			if (match && match.orderstatus) {
				// Nur den Status im bereits angezeigten Ergebnis aktualisieren,
				// Rest (orderId/note) unveraendert lassen.
				setResult((prev) => (prev ? { ...prev, status: match.orderstatus } : prev));
			}
			// Orderbuch/Depot ebenfalls auffrischen - der Status kann sich seit
			// dem ersten notify (direkt nach dem Senden) geaendert haben.
			notifyBrokerageDataChanged(x);
		});
	}

	if (result) {
		return (
			<div className="brokerage-card">
				{props.onDone && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onDone}></i>}
				<h4>{isChange ? 'Order geändert' : 'Order gesendet'}</h4>
				<p>Order-ID {result.orderId} – Status: {result.status || 'Pending'}</p>
				{result.note && <p className="brokerage-hint">{result.note}</p>}
				<button type="button" className="btn btn-primary" onClick={() => props.onDone && props.onDone()}>Schließen</button>
			</div>
		);
	}

	return (
		<div className="brokerage-card" onKeyDown={handleKeyDown}>
			{props.onCancel && <i className="bi bi-x-lg brokerage-card-close" title="Schließen" onClick={props.onCancel}></i>}
			<h4>{isChange ? 'Order ändern' : (action === 'SELL' ? 'Verkaufen' : 'Kaufen')}: {target.label || target.symbol}</h4>
			{/* Kurzfrist-Ampel (Chat vom 2026-08-10) - charttechnische Analyse auf
			    1-Minuten-Basis fuer einen ganz kurzfristigen Trade. Nur wenn aus
			    der Watchlist geoeffnet (internes Symbol fuer den Kerzen-Abruf da). */}
			{target.quoteSymbol &&
				<div className="ib-order-signal">
					<span className="ib-order-signal-caption">Kurzfrist-Analyse</span>
					<span className="ib-traffic" aria-hidden="true">
						<span className={'ib-traffic-dot ib-traffic-red' + (shortTerm && shortTerm.ok && shortTerm.light === 'red' ? ' ib-traffic-on' : '')}></span>
						<span className={'ib-traffic-dot ib-traffic-yellow' + (shortTerm && shortTerm.ok && shortTerm.light === 'yellow' ? ' ib-traffic-on' : '')}></span>
						<span className={'ib-traffic-dot ib-traffic-green' + (shortTerm && shortTerm.ok && shortTerm.light === 'green' ? ' ib-traffic-on' : '')}></span>
					</span>
					<span className="ib-order-signal-text">
						{shortTerm == null
							? 'analysiere…'
							: !shortTerm.ok
								? 'keine Daten'
								: shortTerm.light === 'green'
									? 'bullisch – spricht für Long'
									: shortTerm.light === 'red'
										? 'bärisch – spricht für Short'
										: 'neutral – abwarten'}
					</span>
				</div>
			}
			{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
			{/* Nicht ueber "brokerage-field-row" (display:contents auf Desktop,
			    siehe Brokerage.css) - die beiden Radiobuttons sollen immer in
			    EINER Zeile nebeneinander stehen, nicht wie Label/Feld gestapelt
			    werden. */}
			<div className="brokerage-action-row">
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-action" value="BUY" checked={action === 'BUY'} onChange={() => setAction('BUY')} />
					Kaufen
				</label>
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-action" value="SELL" checked={action === 'SELL'} onChange={() => setAction('SELL')} />
					Verkaufen (Leerverkauf möglich)
				</label>
			</div>
			{/* Chat vom 2026-08-07 (Nutzerwunsch: "kann um jede Sekunde gehen") -
			    Radiobuttons statt Dropdown, analog Kaufen/Verkaufen oben - ein
			    Klick statt Dropdown-oeffnen-dann-Option-treffen. */}
			<div className="brokerage-action-row">
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-type" value="MKT" checked={orderType === 'MKT'} onChange={() => setOrderType('MKT')} />
					Market
				</label>
				<label className="brokerage-radio">
					<input type="radio" name="ib-order-type" value="LMT" checked={orderType === 'LMT'} onChange={() => setOrderType('LMT')} />
					Limit
				</label>
			</div>
			{/* Realtime-Bid/Ask (Chat vom 2026-08-10) - nur wenn aus der Watchlist
			    geoeffnet (Live-Quote verfuegbar). Die fuer die aktuelle
			    Orderrichtung relevante Seite (BUY=Ask, SELL=Bid) ist hervorgehoben,
			    da sie das Limit vorbelegt. */}
			{(liveBid != null || liveAsk != null) &&
				<div className="ib-order-quote">
					<span className={'ib-order-quote-side' + (action === 'SELL' ? ' ib-order-quote-active' : '')}>
						Bid <strong>{formatQuote(liveBid)}</strong>
					</span>
					<span className={'ib-order-quote-side' + (action === 'BUY' ? ' ib-order-quote-active' : '')}>
						Ask <strong>{formatQuote(liveAsk)}</strong>
					</span>
				</div>
			}
			<div className="brokerage-field-row">
				<label>Stück</label>
				<input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
			</div>
			{orderType === 'LMT' &&
				<div className="brokerage-field-row">
					<label>Limit</label>
					{/* Schrittweite kursabhaengig (Chat vom 2026-08-11) - grosse Futures
					    in ganzen Schritten (Nutzerwunsch), Aktien/Penny-Stocks fein,
					    damit "Pfeil runter" bei einer 0,58-Aktie nicht -0,42 ergibt.
					    Manuelle Eingabe bleibt frei (kein Rundungszwang). */}
					<input ref={limitInputRef} type="number" step={limitStep(limitPrice)} min="0" value={limitPrice} onFocus={() => setLimitTouched(true)} onChange={(e) => setLimitPrice(e.target.value)} />
				</div>
			}
			{confirming &&
				<p className="brokerage-hint">
					<i className="bi bi-exclamation-triangle"></i> Diese Order wird bei Bestätigung SOFORT und OHNE weitere Rückfrage bei IB ausgeführt.
				</p>
			}
			<div className="brokerage-order-detail-actions">
				<button type="button" className="btn btn-secondary" onClick={() => props.onCancel && props.onCancel()}>Abbrechen</button>
				<button type="button" className="btn btn-primary" disabled={sending || !quantity} onClick={submit}>
					{sending ? 'Sende…' : (confirming ? 'Jetzt verbindlich senden' : 'Weiter')}
				</button>
			</div>
		</div>
	);
}
